/**
 * Stories — Phase 8f. Curated, deterministic questions that the QA card
 * can replay. Each card click invokes the QA mutation with the
 * pre-written question, so presentations have a stable backbone
 * alongside the open-ended question box. Internal CBO only.
 */

import React from 'react';
import { BookOpen, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useDemoStories, useLegacyTrustQA, type QAResponse } from '@/hooks/ml';
import { STORY_CACHED_ANSWERS } from './storyCachedAnswers';

interface Props {
  onAnswer?: (resp: QAResponse) => void;
}

// Built-in fallback stories — keep parity with backend DEMO_STORIES so
// the card works even when /demo-stories returns 409/500/network error.
// Source of truth: app/routers/legacy_trust_analytics.py DEMO_STORIES.
const FALLBACK_STORIES = [
  { key: 'cchi_mfoa_headline',
    title: 'CCHI / MFOA — the headline story',
    question: 'How did the CCHI/MFOA federation perform overall on Medi-Cal enrollment outreach for older adults, given the population profile?',
    tags: ['federation', 'calibration', 'vulnerability'] },
  { key: 'cchi_vulnerability_band_attribution',
    title: 'Which vulnerability band drove the outcomes?',
    question: 'Which vulnerability band within CCHI/MFOA drove the bulk of projected Medi-Cal enrollments, and why does that matter for the overall narrative?',
    tags: ['vulnerability', 'calibration', 'narrative'] },
  { key: 'cchi_language_equity',
    title: 'Language equity in CCHI outreach',
    question: "How does CCHI's Spanish-language and other non-English outreach compare to the California LEP baseline?",
    tags: ['federation', 'benchmark', 'language'] },
  { key: 'cross_cohort_compare',
    title: 'CCHI vs IFR vs Sycamores vs VyC — head-to-head',
    question: 'Compare engagement rates, reply quality, and projected outcomes across CCHI/MFOA, IFR/ECM, Sycamores/ECM, and VyC/CHW.',
    tags: ['cross-cohort', 'calibration'] },
  { key: 'ecm_funnel_proxy',
    title: 'ECM funnel — IFR + Sycamores',
    question: 'What does the directional ECM funnel look like for IFR and Sycamores under the published CHCS 6-month engagement benchmark?',
    tags: ['calibration', 'benchmark', 'ecm'] },
  { key: 'promotora_model_vyc',
    title: 'VyC promotora-led outreach effectiveness',
    question: "How does VyC's CHW + promotora-led outreach engagement rate compare against industry Medicaid SMS benchmarks, and why is it elevated?",
    tags: ['federation', 'benchmark'] },
  { key: 'monthly_trajectory_2024',
    title: '2024 enrollment trajectory',
    question: 'Show the monthly trajectory of projected Medi-Cal enrollments for CCHI/MFOA across 2024 — when did momentum build?',
    tags: ['time-series', 'calibration'] },
  { key: 'older_adult_reach',
    title: 'Older-adult cohort reach',
    question: "Within CCHI/MFOA's targeted older-adult cohort (DOB ≤1973), how many were reached, and what was the engagement profile?",
    tags: ['federation', 'vulnerability'] },
  { key: 'external_benchmarks_overview',
    title: 'External benchmark overlay',
    question: "Summarize how Trust Marketplace's cohort metrics compare to published KFF, DHCS, Pew, CMS, and CHCS benchmarks across outreach, vulnerability, and outcomes.",
    tags: ['benchmark', 'narrative'] },
];

const TAG_COLOR: Record<string, string> = {
  federation: 'bg-indigo-100 text-indigo-700',
  calibration: 'bg-violet-100 text-violet-700',
  vulnerability: 'bg-rose-100 text-rose-700',
  benchmark: 'bg-amber-100 text-amber-700',
  'cross-cohort': 'bg-teal-100 text-teal-700',
  'time-series': 'bg-sky-100 text-sky-700',
  ecm: 'bg-emerald-100 text-emerald-700',
  language: 'bg-fuchsia-100 text-fuchsia-700',
  'reply-intent': 'bg-pink-100 text-pink-700',
  quality: 'bg-emerald-100 text-emerald-700',
  narrative: 'bg-gray-100 text-gray-700',
};

export const DemoStoriesCard: React.FC<Props> = ({ onAnswer }) => {
  const stories = useDemoStories();
  const qa = useLegacyTrustQA();
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const [activeAnswer, setActiveAnswer] = React.useState<{
    answer: string;
    citations: string[];
    source: 'cached' | 'live';
  } | null>(null);

  const ask = (key: string, question: string) => {
    setActiveKey(key);

    // Step 1: render the cached deterministic answer instantly. The
    // numbers are baked-in from prod observations on 2026-05-21 and
    // are guaranteed to render even if the API is 409 / down / Anthropic
    // is out of credit.
    const cached = STORY_CACHED_ANSWERS[key];
    if (cached) {
      setActiveAnswer({
        answer: cached.answer,
        citations: cached.citations,
        source: 'cached',
      });
    } else {
      setActiveAnswer(null);
    }

    // Step 2: also fire the live QA in the background. If it succeeds,
    // upgrade the displayed answer to the LLM-generated version (which
    // may have more recent numbers from the evidence pack). If it fails
    // (409 deploy in progress, Anthropic credit out, etc), the cached
    // answer above stays visible — no error UX disruption.
    if (qa.isPending) return;
    qa.mutate(
      { question },
      {
        onSuccess: (resp) => {
          if (resp && !resp.error && resp.answer) {
            setActiveAnswer({
              answer: resp.answer,
              citations: resp.citations ?? [],
              source: 'live',
            });
          }
          if (onAnswer) onAnswer(resp);
        },
        // onError: silently keep the cached answer in place
      },
    );
  };

  // Use server list when available, otherwise fall back to the built-in
  // FALLBACK_STORIES so the card always renders.
  const storiesToShow = stories.data?.stories ?? FALLBACK_STORIES;

  if (stories.isLoading && !stories.data) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">Loading demo stories…</CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-1.5 text-xs text-gray-600">
          <BookOpen className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-indigo-500" />
          <span>
            Curated questions with deterministic, cached narrative answers.
            Click any story to render its narrative instantly — the live
            Q&amp;A engine runs in parallel and upgrades the answer if it
            responds.
          </span>
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {storiesToShow.map((s) => {
            const isActive = activeKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => ask(s.key, s.question)}
                className={`group rounded border p-2.5 text-left transition ${
                  isActive
                    ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-300'
                    : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-900">{s.title}</span>
                  <ArrowRight className="h-3 w-3 flex-shrink-0 text-gray-400 group-hover:text-indigo-500" />
                </div>
                <div className="mt-0.5 text-[11px] text-gray-600 line-clamp-2">{s.question}</div>
                {s.tags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${
                          TAG_COLOR[t] ?? 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {activeAnswer && (
          <div className="rounded-md border border-gray-200 bg-gray-50/40 p-3 space-y-1">
            <div className="flex items-center justify-between gap-2 text-[10px] font-medium uppercase text-gray-500">
              <span>Answer</span>
              <span className="flex items-center gap-1 normal-case">
                {activeAnswer.source === 'cached' ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    Cached narrative
                    {qa.isPending && (
                      <span className="text-gray-400">· checking live engine…</span>
                    )}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3 w-3 text-violet-500" />
                    Live {qa.data?.model ?? 'engine'}
                  </>
                )}
              </span>
            </div>
            <div className="whitespace-pre-wrap text-xs text-gray-800 leading-relaxed">
              {activeAnswer.answer}
            </div>
            {activeAnswer.citations.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 pt-1">
                <span className="text-[10px] text-gray-500">Sources:</span>
                {activeAnswer.citations.map((c) => (
                  <code
                    key={c}
                    className="rounded bg-gray-100 px-1.5 py-0.5 text-[9px] font-mono text-gray-700"
                  >
                    {c}
                  </code>
                ))}
              </div>
            )}
            {qa.isError && activeAnswer.source === 'cached' && (
              <div className="flex items-start gap-1 pt-1 text-[10px] text-amber-700">
                <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                Live Q&amp;A engine is unreachable right now (likely a backend
                deploy in progress). Cached narrative shown above — numbers
                are accurate as of 2026-05-21.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DemoStoriesCard;
